// PaidTransactionsList.jsx
import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../module_roots/AuthContext';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
    faMoneyBillWave,
    faUser,
    faCalendarAlt,
    faSearch,
    faFileInvoice,
    faCheckCircle,
    faTimes,
    faSpinner,
    faPrint,
    faDownload,
    faEye,
    faChevronLeft,
    faChevronRight,
    faFilter,
    faExclamationTriangle,
    faRupeeSign,
    faArrowDown,
    faArrowUp,
    faSort,
    faSortUp,
    faSortDown,
    faSyncAlt,
    faReceipt,
    faWallet,
    faChartBar,
    faBalanceScale
} from '@fortawesome/free-solid-svg-icons';
import "../styles/CustomerList.css";
import "../styles/Customer.css";

const PaidTransactionsList = () => {
    const { user, currentShop } = useAuth();
    const [transactions, setTransactions] = useState([]);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);
    const [searchTerm, setSearchTerm] = useState('');
    const [filterStatus, setFilterStatus] = useState('all');
    const [filterPaymentMethod, setFilterPaymentMethod] = useState('all');
    const [filterCustomer, setFilterCustomer] = useState('');
    const [customers, setCustomers] = useState([]);
    const [currentPage, setCurrentPage] = useState(1);
    const [itemsPerPage] = useState(20);
    const [totalCount, setTotalCount] = useState(0);
    const [sortField, setSortField] = useState('sale_date');
    const [sortDirection, setSortDirection] = useState('desc');
    const [selectedTransaction, setSelectedTransaction] = useState(null);
    const [showDetailsModal, setShowDetailsModal] = useState(false);
    const [showFilters, setShowFilters] = useState(false);
    const [summary, setSummary] = useState({
        total_paid: 0,
        total_partial: 0,
        total_unpaid: 0,
        total_amount: 0
    });
    const [shopId, setShopId] = useState(() => currentShop?.shop_id || user?.shop_id || localStorage.getItem('current_shop_id') || '');

    const formatCurrency = (amount) => {
        const num = parseFloat(amount) || 0;
        return new Intl.NumberFormat('en-PK', {
            style: 'currency',
            currency: 'PKR',
            minimumFractionDigits: 0,
            maximumFractionDigits: 0
        }).format(num);
    };

    const formatDate = (dateString) => {
        if (!dateString) return '-';
        return new Date(dateString).toLocaleDateString('en-PK', {
            day: '2-digit',
            month: 'short',
            year: 'numeric'
        });
    };

    const loadPaidTransactions = useCallback(async () => {
        if (!shopId) {
            setError('No shop selected');
            return;
        }

        setLoading(true);
        setError(null);

        try {
            const payload = {
                action: 'get_records',
                shop_id: parseInt(shopId),
                limit: 9999
            };

            const response = await fetch('https://ourzestapp.online/oz_backend/fab_sale.php', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify(payload)
            });

            const data = await response.json();

            if (data.status === 'success') {
                let sales = data.data || [];

                let filteredSales = sales.filter(sale => {
                    const balance = parseFloat(sale.balance) || 0;
                    const paidAmount = parseFloat(sale.paid_amount) || 0;
                    
                    if (filterStatus === 'paid') {
                        return balance <= 0 && paidAmount > 0;
                    }
                    if (filterStatus === 'partial') {
                        return balance > 0 && paidAmount > 0;
                    }
                    if (filterStatus === 'unpaid') {
                        return balance > 0 && paidAmount <= 0;
                    }
                    return paidAmount > 0;
                });

                if (filterPaymentMethod !== 'all') {
                    filteredSales = filteredSales.filter(sale => 
                        sale.payment_method === filterPaymentMethod
                    );
                }

                if (filterCustomer && filterCustomer.trim()) {
                    const searchTermLower = filterCustomer.toLowerCase().trim();
                    filteredSales = filteredSales.filter(sale =>
                        sale.customer_name?.toLowerCase().includes(searchTermLower)
                    );
                }

                if (searchTerm && searchTerm.trim()) {
                    const searchLower = searchTerm.toLowerCase().trim();
                    filteredSales = filteredSales.filter(sale =>
                        sale.bill_number?.toLowerCase().includes(searchLower) ||
                        sale.customer_name?.toLowerCase().includes(searchLower) ||
                        sale.customer_phone?.includes(searchLower)
                    );
                }

                const totalPaid = filteredSales.reduce((sum, sale) => sum + (parseFloat(sale.paid_amount) || 0), 0);
                const totalPartial = filteredSales.filter(s => parseFloat(s.balance) > 0 && parseFloat(s.paid_amount) > 0).length;
                const totalUnpaid = filteredSales.filter(s => parseFloat(s.balance) > 0 && parseFloat(s.paid_amount) <= 0).length;
                const totalAmount = filteredSales.reduce((sum, sale) => sum + (parseFloat(sale.grand_total) || 0), 0);

                setSummary({
                    total_paid: totalPaid,
                    total_partial: totalPartial,
                    total_unpaid: totalUnpaid,
                    total_amount: totalAmount
                });

                filteredSales.sort((a, b) => {
                    let valA = a[sortField] || '';
                    let valB = b[sortField] || '';
                    
                    if (sortField === 'sale_date' || sortField === 'created_at') {
                        valA = new Date(valA);
                        valB = new Date(valB);
                    } else if (sortField === 'grand_total' || sortField === 'paid_amount' || sortField === 'balance') {
                        valA = parseFloat(valA) || 0;
                        valB = parseFloat(valB) || 0;
                    } else {
                        valA = String(valA).toLowerCase();
                        valB = String(valB).toLowerCase();
                    }

                    if (valA < valB) return sortDirection === 'asc' ? -1 : 1;
                    if (valA > valB) return sortDirection === 'asc' ? 1 : -1;
                    return 0;
                });

                setTotalCount(filteredSales.length);
                
                const startIndex = (currentPage - 1) * itemsPerPage;
                const paginatedSales = filteredSales.slice(startIndex, startIndex + itemsPerPage);
                setTransactions(paginatedSales);

                const uniqueCustomers = [...new Set(sales.map(s => s.customer_name).filter(Boolean))];
                setCustomers(uniqueCustomers);

            } else {
                setError(data.message || 'Failed to load transactions');
            }
        } catch (err) {
            console.error('Error loading paid transactions:', err);
            setError('Network error: ' + err.message);
        } finally {
            setLoading(false);
        }
    }, [shopId, filterStatus, filterPaymentMethod, filterCustomer, searchTerm, sortField, sortDirection, currentPage, itemsPerPage]);

    useEffect(() => {
        if (shopId) {
            loadPaidTransactions();
        }
    }, [loadPaidTransactions]);

    const handleSort = (field) => {
        if (sortField === field) {
            setSortDirection(prev => prev === 'asc' ? 'desc' : 'asc');
        } else {
            setSortField(field);
            setSortDirection('asc');
        }
        setCurrentPage(1);
    };

    const resetFilters = () => {
        setFilterStatus('all');
        setFilterPaymentMethod('all');
        setFilterCustomer('');
        setSearchTerm('');
        setCurrentPage(1);
    };

    const totalPages = Math.ceil(totalCount / itemsPerPage);

    const getStatusBadge = (sale) => {
        const balance = parseFloat(sale.balance) || 0;
        const paidAmount = parseFloat(sale.paid_amount) || 0;
        
        if (balance <= 0 && paidAmount > 0) {
            return <span className="status-badge paid"><FontAwesomeIcon icon={faCheckCircle} /> Paid</span>;
        } else if (balance > 0 && paidAmount > 0) {
            return <span className="status-badge partial"><FontAwesomeIcon icon={faArrowDown} /> Partial</span>;
        } else {
            return <span className="status-badge unpaid"><FontAwesomeIcon icon={faTimes} /> Unpaid</span>;
        }
    };

    const getPaymentMethodBadge = (method) => {
        const methods = {
            cash: { label: 'Cash', icon: faMoneyBillWave, color: '#10b981' },
            bank_transfer: { label: 'Bank Transfer', icon: faArrowUp, color: '#3b82f6' },
            cheque: { label: 'Cheque', icon: faFileInvoice, color: '#8b5cf6' }
        };
        const m = methods[method] || methods.cash;
        return (
            <span className="payment-method-badge" style={{ backgroundColor: `${m.color}20`, color: m.color }}>
                <FontAwesomeIcon icon={m.icon} /> {m.label}
            </span>
        );
    };

    return (
        <div className="customer-list-modern">
            {/* Summary Cards */}
            <div className="summary-stats-modern">
                <div className="stat-card">
                    <div className="stat-icon" style={{ backgroundColor: 'rgba(16, 185, 129, 0.1)' }}>
                        <FontAwesomeIcon icon={faWallet} style={{ color: '#10b981' }} />
                    </div>
                    <div className="stat-content">
                        <div className="stat-value" style={{ color: '#10b981' }}>{formatCurrency(summary.total_paid)}</div>
                        <div className="stat-label">Total Paid Amount</div>
                    </div>
                </div>
                <div className="stat-card">
                    <div className="stat-icon" style={{ backgroundColor: 'rgba(245, 158, 11, 0.1)' }}>
                        <FontAwesomeIcon icon={faArrowDown} style={{ color: '#f59e0b' }} />
                    </div>
                    <div className="stat-content">
                        <div className="stat-value" style={{ color: '#f59e0b' }}>{summary.total_partial}</div>
                        <div className="stat-label">Partial Payments</div>
                    </div>
                </div>
                <div className="stat-card">
                    <div className="stat-icon" style={{ backgroundColor: 'rgba(239, 68, 68, 0.1)' }}>
                        <FontAwesomeIcon icon={faTimes} style={{ color: '#ef4444' }} />
                    </div>
                    <div className="stat-content">
                        <div className="stat-value" style={{ color: '#ef4444' }}>{summary.total_unpaid}</div>
                        <div className="stat-label">Unpaid</div>
                    </div>
                </div>
                <div className="stat-card">
                    <div className="stat-icon" style={{ backgroundColor: 'rgba(59, 130, 246, 0.1)' }}>
                        <FontAwesomeIcon icon={faReceipt} style={{ color: '#3b82f6' }} />
                    </div>
                    <div className="stat-content">
                        <div className="stat-value">{formatCurrency(summary.total_amount)}</div>
                        <div className="stat-label">Total Sale Amount</div>
                    </div>
                </div>
            </div>

            {/* Filter Bar */}
            <div className="filters-row">
                <div className="filter-group compact">
                    <button
                        className={`filter-toggle ${showFilters ? 'active' : ''}`}
                        onClick={() => setShowFilters(!showFilters)}
                    >
                        <FontAwesomeIcon icon={faFilter} /> Filters
                    </button>
                </div>

                <div className="filter-group compact search-group">
                    <div className="search-box compact">
                        <FontAwesomeIcon icon={faSearch} className="search-icon" />
                        <input
                            type="text"
                            placeholder="Search by bill #, customer..."
                            value={searchTerm}
                            onChange={(e) => {
                                setSearchTerm(e.target.value);
                                setCurrentPage(1);
                            }}
                        />
                    </div>
                </div>

                <div className="filter-group compact">
                    <div className="filter-tabs">
                        <button
                            className={`filter-tab ${filterStatus === 'all' ? 'active' : ''}`}
                            onClick={() => {
                                setFilterStatus('all');
                                setCurrentPage(1);
                            }}
                        >
                            All Paid
                        </button>
                        <button
                            className={`filter-tab paid ${filterStatus === 'paid' ? 'active' : ''}`}
                            onClick={() => {
                                setFilterStatus('paid');
                                setCurrentPage(1);
                            }}
                        >
                            <FontAwesomeIcon icon={faCheckCircle} /> Fully Paid
                        </button>
                        <button
                            className={`filter-tab partial ${filterStatus === 'partial' ? 'active' : ''}`}
                            onClick={() => {
                                setFilterStatus('partial');
                                setCurrentPage(1);
                            }}
                        >
                            <FontAwesomeIcon icon={faArrowDown} /> Partial
                        </button>
                    </div>
                </div>

                <div className="filter-group compact">
                    <button className="refresh-btn-modern compact" onClick={loadPaidTransactions}>
                        <FontAwesomeIcon icon={faSyncAlt} spin={loading} />
                    </button>
                </div>
            </div>

            {/* Advanced Filters */}
            {showFilters && (
                <div className="advanced-filters-section">
                    <div className="filters-row">
                        <div className="filter-group compact">
                            <select
                                value={filterPaymentMethod}
                                onChange={(e) => {
                                    setFilterPaymentMethod(e.target.value);
                                    setCurrentPage(1);
                                }}
                                className="filter-select"
                            >
                                <option value="all">All Methods</option>
                                <option value="cash">Cash</option>
                                <option value="bank_transfer">Bank Transfer</option>
                                <option value="cheque">Cheque</option>
                            </select>
                        </div>

                        <div className="filter-group compact">
                            <select
                                value={filterCustomer}
                                onChange={(e) => {
                                    setFilterCustomer(e.target.value);
                                    setCurrentPage(1);
                                }}
                                className="filter-select"
                            >
                                <option value="">All Customers</option>
                                {customers.map(customer => (
                                    <option key={customer} value={customer}>{customer}</option>
                                ))}
                            </select>
                        </div>

                        <div className="filter-group compact">
                            <button className="refresh-btn-modern compact" onClick={resetFilters} style={{ background: '#6b7280' }}>
                                <FontAwesomeIcon icon={faTimes} />
                                <span className="btn-text">Clear</span>
                            </button>
                        </div>

                        <div className="filter-group compact">
                            <button className="refresh-btn-modern compact" onClick={loadPaidTransactions} style={{ background: 'var(--primary-gradient, linear-gradient(135deg, #8b5cf6, #7c3aed))' }}>
                                <FontAwesomeIcon icon={faCheckCircle} />
                                <span className="btn-text">Apply</span>
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {/* Table Section */}
            <div className="table-scroll-container">
                <div className="table-container-modern">
                    <div className="customer-table-wrapper">
                        {loading ? (
                            <div className="loading-container">
                                <div className="loading-spinner-modern"></div>
                                <p>Loading paid transactions...</p>
                            </div>
                        ) : error ? (
                            <div className="no-data-container">
                                <div className="no-data-message">
                                    <FontAwesomeIcon icon={faExclamationTriangle} />
                                    <h3>Error</h3>
                                    <p>{error}</p>
                                    <button className="btn-modern btn-primary-modern" onClick={loadPaidTransactions}>Retry</button>
                                </div>
                            </div>
                        ) : transactions.length === 0 ? (
                            <div className="no-data-container">
                                <div className="no-data-message">
                                    <FontAwesomeIcon icon={faReceipt} style={{ color: '#6b7280' }} />
                                    <h3>No Paid Transactions Found</h3>
                                    <p>Try adjusting your filters</p>
                                </div>
                            </div>
                        ) : (
                            <table className="customer-table-modern">
                                <thead>
                                    <tr>
                                        <th className="centered-cell" onClick={() => handleSort('bill_number')}>
                                            Bill # {sortField === 'bill_number' && <FontAwesomeIcon icon={sortDirection === 'asc' ? faSortUp : faSortDown} />}
                                        </th>
                                        <th className="centered-cell" onClick={() => handleSort('sale_date')}>
                                            Date {sortField === 'sale_date' && <FontAwesomeIcon icon={sortDirection === 'asc' ? faSortUp : faSortDown} />}
                                        </th>
                                        <th className="centered-cell" onClick={() => handleSort('customer_name')}>
                                            Customer {sortField === 'customer_name' && <FontAwesomeIcon icon={sortDirection === 'asc' ? faSortUp : faSortDown} />}
                                        </th>
                                        <th className="centered-cell" onClick={() => handleSort('grand_total')}>
                                            Total {sortField === 'grand_total' && <FontAwesomeIcon icon={sortDirection === 'asc' ? faSortUp : faSortDown} />}
                                        </th>
                                        <th className="centered-cell" onClick={() => handleSort('paid_amount')}>
                                            Paid {sortField === 'paid_amount' && <FontAwesomeIcon icon={sortDirection === 'asc' ? faSortUp : faSortDown} />}
                                        </th>
                                        <th className="centered-cell" onClick={() => handleSort('balance')}>
                                            Balance {sortField === 'balance' && <FontAwesomeIcon icon={sortDirection === 'asc' ? faSortUp : faSortDown} />}
                                        </th>
                                        <th className="centered-cell">Status</th>
                                        <th className="centered-cell">Payment Method</th>
                                        <th className="actions-column centered-cell">Actions</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {transactions.map(sale => (
                                        <tr key={sale.id}>
                                            <td className="centered-cell">
                                                <span className="cell-text">{sale.bill_number}</span>
                                            </td>
                                            <td className="centered-cell">{formatDate(sale.sale_date)}</td>
                                            <td className="centered-cell">
                                                <div className="customer-info-cell">
                                                    <span>{sale.customer_name}</span>
                                                    {sale.customer_phone && (
                                                        <span className="customer-phone">{sale.customer_phone}</span>
                                                    )}
                                                </div>
                                            </td>
                                            <td className="centered-cell amount-cell">
                                                {formatCurrency(sale.grand_total)}
                                            </td>
                                            <td className="centered-cell amount-cell paid-amount">
                                                {formatCurrency(sale.paid_amount)}
                                            </td>
                                            <td className={`centered-cell amount-cell ${parseFloat(sale.balance) > 0 ? 'balance-due' : 'balance-zero'}`}>
                                                {formatCurrency(sale.balance)}
                                            </td>
                                            <td className="centered-cell">{getStatusBadge(sale)}</td>
                                            <td className="centered-cell">
                                                {getPaymentMethodBadge(sale.payment_method)}
                                            </td>
                                            <td className="actions-cell-modern centered-cell">
                                                <div className="actions-content">
                                                    <button
                                                        onClick={() => {
                                                            setSelectedTransaction(sale);
                                                            setShowDetailsModal(true);
                                                        }}
                                                        className="action-btn-modern view-btn-modern"
                                                        title="View details"
                                                    >
                                                        <FontAwesomeIcon icon={faEye} />
                                                    </button>
                                                </div>
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        )}
                    </div>
                </div>
            </div>

            {/* Pagination */}
            {totalPages > 1 && (
                <div className="pagination-modern">
                    <div className="pagination-info">
                        <span>Showing {((currentPage - 1) * itemsPerPage) + 1}-{Math.min(currentPage * itemsPerPage, totalCount)} of {totalCount}</span>
                    </div>
                    <div className="pagination-controls">
                        <button onClick={() => setCurrentPage(p => Math.max(1, p - 1))} disabled={currentPage === 1} className="pagination-btn">
                            <FontAwesomeIcon icon={faChevronLeft} /> Previous
                        </button>
                        <div className="page-numbers">
                            {Array.from({ length: Math.min(7, totalPages) }, (_, i) => {
                                let pageNum;
                                if (totalPages <= 7) pageNum = i + 1;
                                else if (currentPage <= 4) pageNum = i + 1;
                                else if (currentPage >= totalPages - 3) pageNum = totalPages - 6 + i;
                                else pageNum = currentPage - 3 + i;
                                return (
                                    <button key={pageNum} onClick={() => setCurrentPage(pageNum)} className={`page-number ${currentPage === pageNum ? 'active' : ''}`}>
                                        {pageNum}
                                    </button>
                                );
                            })}
                        </div>
                        <button onClick={() => setCurrentPage(p => Math.min(totalPages, p + 1))} disabled={currentPage === totalPages} className="pagination-btn">
                            Next <FontAwesomeIcon icon={faChevronRight} />
                        </button>
                    </div>
                </div>
            )}

            {/* Details Modal */}
            {showDetailsModal && selectedTransaction && (
                <div className="modal-overlay-modern" onClick={() => setShowDetailsModal(false)}>
                    <div className="modal-content-modern view-modal-content" onClick={e => e.stopPropagation()}>
                        <div className="modal-header-modern">
                            <div className="modal-icon">
                                <FontAwesomeIcon icon={faReceipt} />
                            </div>
                            <div className="modal-title">
                                <h3>Sale Details - {selectedTransaction.bill_number}</h3>
                            </div>
                            <button className="close-btn-modern" onClick={() => setShowDetailsModal(false)}>
                                <FontAwesomeIcon icon={faTimes} />
                            </button>
                        </div>
                        <div className="modal-body-modern view-modal-body">
                            <div className="view-section">
                                <h4 className="view-section-title"><FontAwesomeIcon icon={faInfoCircle} /> General Information</h4>
                                <div className="view-grid two-columns">
                                    <div className="view-row">
                                        <span className="view-label">Bill Number:</span>
                                        <span className="view-value">{selectedTransaction.bill_number}</span>
                                    </div>
                                    <div className="view-row">
                                        <span className="view-label">Date:</span>
                                        <span className="view-value">{formatDate(selectedTransaction.sale_date)}</span>
                                    </div>
                                    <div className="view-row">
                                        <span className="view-label">Customer:</span>
                                        <span className="view-value">{selectedTransaction.customer_name}</span>
                                    </div>
                                    {selectedTransaction.customer_phone && (
                                        <div className="view-row">
                                            <span className="view-label">Phone:</span>
                                            <span className="view-value">{selectedTransaction.customer_phone}</span>
                                        </div>
                                    )}
                                    {selectedTransaction.customer_address && (
                                        <div className="view-row full-width">
                                            <span className="view-label">Address:</span>
                                            <span className="view-value">{selectedTransaction.customer_address}</span>
                                        </div>
                                    )}
                                </div>
                            </div>

                            <div className="view-section">
                                <h4 className="view-section-title"><FontAwesomeIcon icon={faMoneyBillWave} /> Financial Details</h4>
                                <div className="view-grid two-columns">
                                    <div className="view-row">
                                        <span className="view-label">Subtotal:</span>
                                        <span className="view-value">{formatCurrency(selectedTransaction.subtotal)}</span>
                                    </div>
                                    {parseFloat(selectedTransaction.discount_amount) > 0 && (
                                        <div className="view-row">
                                            <span className="view-label">Discount:</span>
                                            <span className="view-value" style={{ color: '#ef4444' }}>-{formatCurrency(selectedTransaction.discount_amount)}</span>
                                        </div>
                                    )}
                                    {parseFloat(selectedTransaction.tax_amount) > 0 && (
                                        <div className="view-row">
                                            <span className="view-label">Tax:</span>
                                            <span className="view-value" style={{ color: '#f59e0b' }}>+{formatCurrency(selectedTransaction.tax_amount)}</span>
                                        </div>
                                    )}
                                    <div className="view-row">
                                        <span className="view-label">Grand Total:</span>
                                        <span className="view-value grand-total">{formatCurrency(selectedTransaction.grand_total)}</span>
                                    </div>
                                    <div className="view-row">
                                        <span className="view-label">Paid Amount:</span>
                                        <span className="view-value paid-amount">{formatCurrency(selectedTransaction.paid_amount)}</span>
                                    </div>
                                    <div className="view-row">
                                        <span className="view-label">Balance:</span>
                                        <span className={`view-value ${parseFloat(selectedTransaction.balance) > 0 ? 'balance-due' : 'balance-zero'}`}>
                                            {formatCurrency(selectedTransaction.balance)}
                                        </span>
                                    </div>
                                    <div className="view-row">
                                        <span className="view-label">Payment Method:</span>
                                        <span className="view-value">{selectedTransaction.payment_method}</span>
                                    </div>
                                    <div className="view-row">
                                        <span className="view-label">Status:</span>
                                        <span className="view-value">{getStatusBadge(selectedTransaction)}</span>
                                    </div>
                                </div>
                            </div>

                            {selectedTransaction.notes && (
                                <div className="view-section">
                                    <h4 className="view-section-title"><FontAwesomeIcon icon={faStickyNote} /> Notes</h4>
                                    <div className="remarks-box">{selectedTransaction.notes}</div>
                                </div>
                            )}

                            {selectedTransaction.items && selectedTransaction.items.length > 0 && (
                                <div className="view-section">
                                    <h4 className="view-section-title"><FontAwesomeIcon icon={faBoxesIcon} /> Items Sold</h4>
                                    <div className="table-scroll-container">
                                        <table className="customer-table-modern">
                                            <thead>
                                                <tr>
                                                    <th className="centered-cell">Product</th>
                                                    <th className="centered-cell">Qty</th>
                                                    <th className="centered-cell">Unit</th>
                                                    <th className="centered-cell">Price</th>
                                                    <th className="centered-cell">Total</th>
                                                </tr>
                                            </thead>
                                            <tbody>
                                                {selectedTransaction.items.map((item, index) => (
                                                    <tr key={index}>
                                                        <td className="centered-cell">{item.product_name}</td>
                                                        <td className="centered-cell">{item.quantity}</td>
                                                        <td className="centered-cell">{item.unit}</td>
                                                        <td className="centered-cell amount-cell">{formatCurrency(item.price_per_unit)}</td>
                                                        <td className="centered-cell amount-cell">{formatCurrency(item.total)}</td>
                                                    </tr>
                                                ))}
                                            </tbody>
                                        </table>
                                    </div>
                                </div>
                            )}
                        </div>
                        <div className="modal-footer-modern">
                            <button className="btn-modern btn-secondary-modern" onClick={() => setShowDetailsModal(false)}>Close</button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default PaidTransactionsList;